import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-extrabold tracking-wide ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border-[3px]",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground border-primary shadow-lg hover:bg-primary/95 hover:shadow-xl hover:scale-[1.02]",
        cta: "bg-cta text-cta-foreground border-cta shadow-lg hover:bg-cta/90 hover:shadow-xl hover:scale-[1.03] font-extrabold",
        destructive: "bg-destructive text-destructive-foreground border-destructive shadow-lg hover:bg-destructive/95 hover:shadow-xl",
        outline: "border-[3px] border-primary bg-primary/5 text-primary font-extrabold hover:bg-primary/15 hover:border-primary",
        secondary: "bg-secondary text-secondary-foreground border-secondary-foreground/30 shadow-lg font-extrabold hover:bg-secondary/90 hover:border-secondary-foreground/50",
        ghost: "border-transparent hover:bg-accent/20 hover:text-accent-foreground hover:border-accent/40 font-extrabold",
        link: "text-primary border-transparent underline-offset-4 hover:underline font-extrabold",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
